import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { newsParser } from '../../util/newsParser';
import { teaserTextParser } from '../../util/teaserTextParser';
import IconExternalLink from '../Icons/IconExternalLink';
import IconSpeechBubble from '../Icons/IconSpeechBubble';
import Card from './Card';

function NewsCardsHighlight({ news, selectedFilter, filterOptions, cssClasses }) {
  const root = useSelector((state) => state.app.root);
  const [selectedNewsItem, setSelectedNewsItem] = useState(null);
  const titleRef = useRef(null);
  const finalCssClasses = cssClasses || '';

  // Memoize parsed news and filtered news
  const parsedNews = useMemo(() => news.map((n) => newsParser(n)), [news]);

  const filteredNews = useMemo(() => {
    if (selectedFilter > 0) {
      const filterSlug = filterOptions[selectedFilter].slug;
      return parsedNews.filter((item) => item.topicsArray.some((t) => t.slug === filterSlug));
    }
    return parsedNews;
  }, [parsedNews, selectedFilter, filterOptions]);

  // Memoize parsed text based on title height
  const parsedText = useMemo(() => {
    if (!selectedNewsItem) return '';
    const title = titleRef.current;
    if (!title) return teaserTextParser(selectedNewsItem.summary, 100);

    const titleHeight = title.offsetHeight;
    if (titleHeight < 30) return teaserTextParser(selectedNewsItem.summary, 180);
    if (titleHeight < 60) return teaserTextParser(selectedNewsItem.summary, 150);
    if (titleHeight < 90) return teaserTextParser(selectedNewsItem.summary, 110);
    return teaserTextParser(selectedNewsItem.summary, 40);
  }, [selectedNewsItem]);

  // Handle news item selection
  const handleNewsItemSelect = useCallback((item) => {
    setSelectedNewsItem(item);
  }, []);

  // Effect to select initial news item
  useEffect(() => {
    if (!selectedNewsItem && filteredNews.length > 0) {
      setSelectedNewsItem(filteredNews[0]);
    }
  }, [filteredNews, selectedNewsItem]);

  // Effect to handle automatic cycling
  useEffect(() => {
    if (filteredNews.length < 2) return;

    const intervalId = setInterval(() => {
      setSelectedNewsItem((prev) => {
        const currentIndex = filteredNews.findIndex((item) => item.slug === prev.slug);
        const nextIndex = (currentIndex + 1) % filteredNews.length;
        return filteredNews[nextIndex];
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [filteredNews]);

  if (!selectedNewsItem || filteredNews.length === 0) return null;

  return (
    <>
      <Card
        cardStyle={'topImage'}
        cssClasses={'card--highlight-active bg-white with-shadow'}
        imagePath={selectedNewsItem.header_picture || null}
        icon={!selectedNewsItem.header_picture && <IconSpeechBubble />}
        linkOnFullCard={true}
        linkTo={selectedNewsItem.external ? selectedNewsItem.source : `${root}/news/${selectedNewsItem.slug}`}
        openLinkInNewTab={selectedNewsItem.external}
      >
        {selectedNewsItem.external && (
          <div className="news-card__label news-card-highlight__label immo-card__label--active">
            <IconExternalLink />
            <span className="text-white">externer Artikel</span>
          </div>
        )}
        <div className="card__content__text">
          <p className="cardMeta mb-50rem">
            {selectedNewsItem.topics.length > 0 && ` ${selectedNewsItem.topics}`}
            {selectedNewsItem.published_on && ` | ${selectedNewsItem.published_on}`}
          </p>
          <p className="font-110 font-weight-600 lh-130 mb-50rem" ref={titleRef}>
            {selectedNewsItem.title}
          </p>
          <div className="news-card__teaser">
            <p className="cardText">{parsedText}</p>
          </div>
        </div>
        <span className="card__readmorelink">Mehr lesen</span>
      </Card>

      <div className={`cardgrid ${finalCssClasses}`}>
        {filteredNews.map((n) => (
          <div className={`news-card ${selectedNewsItem.slug === n.slug ? 'news-card--active' : ''}`} onClick={() => handleNewsItemSelect(n)} key={n.slug}>
            <Card cardStyle="customContent" cssClasses={'bg-white with-shadow'} imagePath={n.header_picture || null} icon={!n.header_picture && <IconSpeechBubble />}>
              <div className="d-flex">
                {n.header_picture && <img src={n.header_picture} alt={n.title} />}
                <div className="card__content__text">
                  <p className="cardMeta">{n.topics.length > 0 && ` ${n.topics}`}</p>
                  <p className="font-110 font-weight-600 lh-130">{n.title}</p>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>
    </>
  );
}

export default React.memo(NewsCardsHighlight);
